import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { clone } from 'ramda';
import { ErrorHandlerService } from '../core/error-handler';
import { UserService } from '../core/user';
import { UserLocation } from '../models/user/location.model';
import { environment } from '../../environments/environment';
import { UnderstandConstants } from './understand.constants';
import { EngageConstants } from '../engage/engage.constants';
import { getFeatureEnabled, State } from '../reducers';
import {
  isRewardMetricsTileUrl,
  mapRewardMetricsTile,
  RewardMetricsAnalytics
} from './reward-metrics-tiles';

interface EventMetrics {
  invitedCount: number | string;
  acceptedCount: number | string;
  declinedCount: number | string;
}

interface EventCampaign {
  campaignId: string;
  eventMetrics: EventMetrics;
}

interface EventMetricsResponse {
  campaigns: EventCampaign[];
}

interface EmailMetrics {
  sentCount: number | string;
  openedCount: number | string;
  clickedCount: number | string;
}

interface EmailCampaign {
  campaignId: string;
  emailMetrics: EmailMetrics;
}

interface EmailMetricsResponse {
  campaigns: EmailCampaign[];
}

export interface EventMetricTotals {
  invitedCount: number;
  acceptedCount: number;
  declinedCount: number;
  eventCount: number;
}

export interface EmailMetricTotals {
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  emailCount: number;
}

interface DrillDownEventMetrics extends EventMetrics {
  noResponseCount: number;
}

interface DrillDownCampaign {
  campaignId: string;
  name: string | null;
  title: string | null;
  status: string | null;
  startDatetime: string | null;
  endDatetime: string | null;
  eventMetrics: DrillDownEventMetrics;
}

interface EventMetricsDrillDownResponse {
  campaigns: DrillDownCampaign[];
  totalCount: number;
}

export interface EventMetricDrillDownRow extends DrillDownEventMetrics {
  campaignId: string;
  name: string | null;
  title: string | null;
  status: string | null;
  startDatetime: string | null;
  endDatetime: string | null;
}

export interface EventMetricDrillDownResult {
  data: EventMetricDrillDownRow[];
  numberOfPages: number;
  totalElements: number;
}

interface DrillDownEmailCampaign {
  campaignId: string;
  name: string | null;
  title: string | null;
  status: string | null;
  startDatetime: string | null;
  endDatetime: string | null;
  emailMetrics: EmailMetrics;
}

interface EmailMetricsDrillDownResponse {
  campaigns: DrillDownEmailCampaign[];
  totalCount: number;
}

export interface EmailMetricDrillDownRow extends EmailMetrics {
  campaignId: string;
  name: string | null;
  title: string | null;
  status: string | null;
  startDatetime: string | null;
  endDatetime: string | null;
}

export interface EmailMetricDrillDownResult {
  data: EmailMetricDrillDownRow[];
  numberOfPages: number;
  totalElements: number;
}

@Injectable()
export class UnderstandService {
  private readonly rewardMetricsRequests = new Map<string, Observable<RewardMetricsAnalytics>>();

  constructor(
    private readonly us: UserService,
    private readonly http: HttpClient,
    private readonly ehs: ErrorHandlerService,
    private readonly store: Store<State>
  ) {}

  public loadUnderstandTiles() {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        let locationNumber = loc.locationNumber;
        const url = `/api/location/${locationNumber}/understand/tiles`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      map((tiles: any[]) => this.enhanceTilesWithNewTile(tiles)),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  private enhanceTilesWithNewTile(tiles: any[]): any[] {
    const newTileIds = UnderstandConstants.newTileIds;
    return tiles.map(tile => {
      if (newTileIds.includes(tile.id)) {
        return {
          ...tile,
          displayNewTile: true,
          newTileTooltip: UnderstandConstants.newTileTooltip
        };
      }
      return tile;
    });
  }

  public getUnderstandTileData(tile) {
    if (tile.url === 'eventStats') {
      return this.store.select(getFeatureEnabled(EngageConstants.eventMetricsFlag)).pipe(
        take(1),
        switchMap((isEventMetricsEnabled) => isEventMetricsEnabled
          ? this.getEventMetricsTileData(tile)
          : this.getLegacyTileData(tile)
        )
      );
    }

    if (tile.url === 'emailStatsV2') {
      return this.store.select(getFeatureEnabled(EngageConstants.emailMetricsFlag)).pipe(
        take(1),
        switchMap((isEmailMetricsEnabled) => isEmailMetricsEnabled
          ? this.getEmailMetricsTileData(tile)
          : this.getLegacyTileData(tile)
        )
      );
    }

    if (isRewardMetricsTileUrl(tile.url)) {
      return this.store.select(getFeatureEnabled(EngageConstants.rewardMetricsFlag)).pipe(
        take(1),
        switchMap((isRewardMetricsEnabled) => isRewardMetricsEnabled
          ? this.getRewardMetricsTileData(tile)
          : this.getLegacyTileData(tile)
        )
      );
    }

    return this.getLegacyTileData(tile);
  }

  private getRewardMetricsTileData(tile) {
    return this.loadRewardAnalytics(tile).pipe(
      map((analytics) => mapRewardMetricsTile(tile.url, analytics)),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  // The three reward tiles share one payload. Parallel tile loads reuse the in-flight request.
  private loadRewardAnalytics(tile): Observable<RewardMetricsAnalytics> {
    const startDate = tile.startDate.format('YYYY-MM-DD[T]00:00:00[Z]');
    const endDate = clone(tile.endDate).add(1, 'd').format('YYYY-MM-DD[T]00:00:00[Z]');
    const excludeCem = tile.excludeCemRewards !== false;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const cacheKey = `${loc.locationNumber}|${startDate}|${endDate}|${excludeCem}`;
        const cached = this.rewardMetricsRequests.get(cacheKey);
        if (cached) {
          return cached;
        }

        const url = `${environment.eventMetricsUrl}/api/reward-metrics?startDate=${encodeURIComponent(startDate)}` +
          `&endDate=${encodeURIComponent(endDate)}&isCemCheck=${excludeCem}`;
        const request$ = this.http.get<RewardMetricsAnalytics>(url, {
          headers: new HttpHeaders({
            'Requested-Localities': loc.locationNumber
          })
        }).pipe(
          tap({ error: () => this.rewardMetricsRequests.delete(cacheKey) }),
          shareReplay(1)
        );
        this.rewardMetricsRequests.clear();
        this.rewardMetricsRequests.set(cacheKey, request$);
        return request$;
      })
    );
  }

  private getEventMetricsTileData(tile) {
    const startDate = tile.startDate.format('YYYY-MM-DD[T]00:00:00[Z]');
    const endDate = clone(tile.endDate).add(1, 'd').format('YYYY-MM-DD[T]00:00:00[Z]');
    const url = `${environment.eventMetricsUrl}/api/event-metrics?startDate=${encodeURIComponent(startDate)}` +
      `&endDate=${encodeURIComponent(endDate)}`;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => this.http.get<EventMetricsResponse>(url, {
        headers: new HttpHeaders({
          'Requested-Localities': loc.locationNumber
        })
      })),
      map((res: EventMetricsResponse) => res.campaigns.reduce<EventMetricTotals>((totals, campaign) => ({
        invitedCount: totals.invitedCount + Number(campaign.eventMetrics.invitedCount),
        acceptedCount: totals.acceptedCount + Number(campaign.eventMetrics.acceptedCount),
        declinedCount: totals.declinedCount + Number(campaign.eventMetrics.declinedCount),
        eventCount: totals.eventCount
      }), {
        invitedCount: 0,
        acceptedCount: 0,
        declinedCount: 0,
        eventCount: res.campaigns.length
      })),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  private getEmailMetricsTileData(tile) {
    const startDate = tile.startDate.format('YYYY-MM-DD[T]00:00:00[Z]');
    const endDate = clone(tile.endDate).add(1, 'd').format('YYYY-MM-DD[T]00:00:00[Z]');
    const url = `${environment.eventMetricsUrl}/api/email-metrics?startDate=${encodeURIComponent(startDate)}` +
      `&endDate=${encodeURIComponent(endDate)}`;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => this.http.get<EmailMetricsResponse>(url, {
        headers: new HttpHeaders({
          'Requested-Localities': loc.locationNumber
        })
      })),
      map((res: EmailMetricsResponse) => res.campaigns.reduce<EmailMetricTotals>((totals, campaign) => ({
        sentCount: totals.sentCount + Number(campaign.emailMetrics.sentCount),
        openedCount: totals.openedCount + Number(campaign.emailMetrics.openedCount),
        clickedCount: totals.clickedCount + Number(campaign.emailMetrics.clickedCount),
        emailCount: totals.emailCount
      }), {
        sentCount: 0,
        openedCount: 0,
        clickedCount: 0,
        emailCount: res.campaigns.length
      })),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  private getLegacyTileData(tile) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        const onePlus = clone(tile.endDate).add(1, 'd');
        const fromDate = tile.startDate.format('YYYY-MM-DD');
        const toDate = onePlus.format('YYYY-MM-DD');
        const isCemCheck = tile.excludeCemRewards;
        let url =
          `/api/location/${locationNumber}/${tile.url}?fromDate=${fromDate}&toDate=${toDate}`;
        if (tile.category === 'treat') {
          url += `&isCemCheck=${isCemCheck}`;
        }
        if (tile.id === 'topCampaignsByRedemptionRate') {
          return this.http.post(environment.spotlightApiUrl + url, {
            page: 0,
            limit: 3,
            sortBy: 'sameLocOffersRedeemPct',
            sortOrder: 'desc',
            filters: [],
            searchBy: null,
            searchTerm: '',
            fromDate,
            toDate
          });
        } else {
          return this.http.get(environment.spotlightApiUrl + url);
        }
      }),
      map((res: any) => res.responseObject),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  public getEmailMetricsDrillDown(config): Observable<EmailMetricDrillDownResult> {
    const startDate = config.startDate.format('YYYY-MM-DD[T]00:00:00[Z]');
    const endDate = clone(config.endDate).add(1, 'd').format('YYYY-MM-DD[T]00:00:00[Z]');
    const url = `${environment.eventMetricsUrl}/api/email-metrics/drilldown?startDate=${encodeURIComponent(startDate)}` +
      `&endDate=${encodeURIComponent(endDate)}`;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => this.http.get<EmailMetricsDrillDownResponse>(url, {
        headers: new HttpHeaders({
          'Requested-Localities': loc.locationNumber
        })
      })),
      map((res) => this.paginateDrillDown(this.flattenEmailMetricsRows(res.campaigns), res.totalCount, config)),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  // Flattens emailMetrics onto the row since the drilldown table reads row[col.prop] with no nested-path support
  private flattenEmailMetricsRows(campaigns: DrillDownEmailCampaign[]): EmailMetricDrillDownRow[] {
    return campaigns.map(({ emailMetrics, ...campaign }) => ({
      ...campaign,
      ...emailMetrics
    }));
  }

  public getUnderstandDrillDownConfig(drillDownId) {
    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => {
        const locationNumber = loc.locationNumber;
        let url =
          `/api/location/${locationNumber}/understand/drilldown?drilldownId=${drillDownId}`;
        return this.http.get(environment.spotlightApiUrl + url);
      }),
      map((res: any) => res.responseObject),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  // Sibling route to eventStats tile call; campaign-service enrichment/null-fallback happens server-side
  public getEventMetricsDrillDown(config): Observable<EventMetricDrillDownResult> {
    const startDate = config.startDate.format('YYYY-MM-DD[T]00:00:00[Z]');
    const endDate = clone(config.endDate).add(1, 'd').format('YYYY-MM-DD[T]00:00:00[Z]');
    const url = `${environment.eventMetricsUrl}/api/event-metrics/drilldown?startDate=${encodeURIComponent(startDate)}` +
      `&endDate=${encodeURIComponent(endDate)}`;

    return this.us.getSelectedLocation().pipe(
      switchMap((loc: UserLocation) => this.http.get<EventMetricsDrillDownResponse>(url, {
        headers: new HttpHeaders({
          'Requested-Localities': loc.locationNumber
        })
      })),
      map((res) => this.paginateDrillDown(this.flattenEventMetricsRows(res.campaigns), res.totalCount, config)),
      catchError((err) => this.ehs.handleError(err))
    );
  }

  // Flattens eventMetrics onto the row since the drilldown table reads row[col.prop] with no nested-path support
  private flattenEventMetricsRows(campaigns: DrillDownCampaign[]): EventMetricDrillDownRow[] {
    return campaigns.map(({ eventMetrics, ...campaign }) => ({
      ...campaign,
      ...eventMetrics
    }));
  }

  // Sorts (falling back to defaultSortBy) then slices the requested page.
  private paginateDrillDown<T>(rows: T[], totalCount: number, config): { data: T[]; numberOfPages: number; totalElements: number } {
    const page = config.page || 0;
    const pageSize = config.pageSize || totalCount || 1;
    const start = page * pageSize;

    const sortBy = config.sortBy || config.defaultSortBy?.prop;
    const sortOrder = config.sortOrder || config.defaultSortBy?.order;
    const sortedRows = this.sortDrillDownRows(rows, sortBy, sortOrder);
    const data = config.serverPaging
      ? sortedRows.slice(start, start + pageSize)
      : sortedRows;

    return {
      data,
      totalElements: totalCount,
      numberOfPages: Math.max(Math.ceil(totalCount / pageSize), 1)
    };
  }

  private sortDrillDownRows<T>(rows: T[], sortBy: string, sortOrder: string): T[] {
    if (!sortBy) {
      return rows;
    }
    const direction = sortOrder === 'desc' ? -1 : 1;
    return [...rows].sort((a: any, b: any) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      if (aVal == null) {
        return bVal == null ? 0 : 1;
      }
      if (bVal == null) {
        return -1;
      }
      if (aVal < bVal) {
        return -direction;
      }
      if (aVal > bVal) {
        return direction;
      }
      return 0;
    });
  }
}
